import { EventEmitter, Injectable, Output } from '@angular/core';
import { catchError, map } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject, throwError } from 'rxjs';
import { BrowserStorageService } from '../browser-storage/browser-storage.service';
import { HttpFunctionsService } from '../http-services/http-functions.service';
import { SessionStorageModel } from 'src/app/models/session-storage';
import { UserDetails } from 'src/app/models/user-details.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  authBaseUrl: string = 'auth/';
  countryUrl: string = '../../assets/countries/countries_en.json';
  @Output() isOpenMobileMenu: EventEmitter<any> = new EventEmitter();
  currentUserDetails: BehaviorSubject<any> = new BehaviorSubject('');
  profileImages: BehaviorSubject<any> = new BehaviorSubject('');
  currentProfilePic = this.profileImages.asObservable(); 
  private googleLoginEvent: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  private googleLoginEventObservable = this.googleLoginEvent.asObservable();
  isNowProducer: BehaviorSubject<any> = new BehaviorSubject(null);
  isOpenSideBar: boolean = false;
  closeAllDialogBox: BehaviorSubject<boolean> =
  new BehaviorSubject<boolean>(true);
  constructor(
    private httpMethods: HttpFunctionsService,
    private setSessionStorage: BrowserStorageService
  ) {
    this.googleLoginEventObservable.subscribe((r) => {});
  }

  emitGoogleLoginEvent(token: string) {
    this.googleLoginEvent.next(token);
  }

  setCurrentProfilePic(profilePicUrl: string) {
    this.profileImages.next(profilePicUrl);
  }

  getCurrentProfilePic(): Observable<string> {
    return this.currentProfilePic;
  }

  googleLoginEventListener() {
    return this.googleLoginEventObservable;
  }

  clearSocialTokens() {
    this.emitGoogleLoginEvent('');
  }

  /**
   * User login
   * @param userData
   * @returns
   */
  userLogin(userData: { email: string; password: string }) {
    return this.httpMethods
      .postMethod(this.authBaseUrl + 'signin', userData)
      .pipe(
        map((response) => {
          if (response) {
            let data = response?.data;
            if (data && Object.keys(data).length) {
              if (data?.user?.is_email_verified) {
                this.setStorageData(response);
              }
            }
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * Request OTP for passwordless email signin
   * @param email
   * @returns
   */
  signInWithOtp(email: string) {
    return this.httpMethods.postMethod(this.authBaseUrl + 'signin-with-otp', {
      email
    });
  }

  /**
   * User register
   * @param userData
   * @returns
   */
  userRegister(userData: any) {
    const normalizedPasswordConfirm =
      userData?.passwordConfirm ||
      userData?.password_confirm ||
      userData?.password ||
      '';
    const payload = {
      ...userData,
      passwordConfirm: normalizedPasswordConfirm,
      role: userData?.role || 'customer',
      password_confirm:
        userData?.password_confirm || normalizedPasswordConfirm
    };
    return this.httpMethods.postMethod(this.authBaseUrl + 'signup', payload);
  }

  /**
   * Send Verification Code
   * @param sendData
   * @returns
   */
  sendVerificationCode(userId: string) {
    let sendData = {
      user_id: userId,
    };
    return this.httpMethods.postMethod(
      this.authBaseUrl + 'send-email-verfication',
      sendData
    );
  }

  /**
   * Otp Verification code
   * @param sendData
   * @returns
   */
  verificationCode(sendData: any, typeConfirmation: string = '') {
    return this.httpMethods
      .postMethod(this.authBaseUrl + 'verify-email', sendData)
      .pipe(
        map((response) => {
          if (response && typeConfirmation) {
            let data = response?.data;
            if (data && Object.keys(data).length) {
              this.setStorageData(response);
            }
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  sendMobileVerification(sendData: { isd: string; number: string }) {
    return this.httpMethods.postMethod(
      this.authBaseUrl + 'send-mobile-verification',
      sendData
    );
  }

  verifyMobileVerification(code: string) {
    return this.httpMethods
      .postMethod(this.authBaseUrl + 'verify-mobile', { code })
      .pipe(
        map((response) => {
          if (response?.data?.user) {
            this.currentUserDetails.next(response.data.user);
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * added producer info
   * @param producerData
   * @returns
   */
  addInfoProducer(producerData: any) {
    return this.httpMethods
      .postMethod(this.authBaseUrl + 'become-a-producer', producerData)
      .pipe(
        map((response) => {
          if (response) {
            this.setStorageData(response);
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  /**
   * set data into the storage
   * @param res
   */
  setStorageData(res: any) {
    let userId = res?.data.user.role === "producer" ? res?.data?.user?.detail?._id : res?.data?.user?._id
    const storeData: SessionStorageModel = {
      access_token: res?.access_token,
      refresh_token: res?.refresh_token,
      isSellerRedirectDashBoard: false,
      role: res?.data.user.role,
      userId: userId
    };
    this.setSessionStorage.setSessionData(storeData);
    this.setSessionStorage.deletePlayCount();
  }

  /**
   * get countries list from the assets
   * @returns Observable
   */
  countryFormAssets() {
    return this.httpMethods.getDataFromAssets(this.countryUrl);
  }

  /**
   * seller Term and Condition accept
   * @returns Observable
   */
  isAcceptTermConditionSeller() {
    return this.httpMethods.postMethod(this.authBaseUrl + 'accept-terms', {});
  }

  /**
   * In this function refresh The Token
   * @returns
   */
  refreshToken(token: string) {
    const sendData = {
      refresh_token: token,
    };
    return this.httpMethods.postMethod(this.authBaseUrl + 'refresh', sendData);
  }

  /**
   * Signing with google and facebook
   * @param type
   * @param tokenId
   * @returns
   */
  signInWithSocial(type: string, tokenId: string) {
    let loginUrlType = 'google-signin';
    if (type === 'facebook') {
      loginUrlType = 'facebook-signin';
    }
    let data = {
      token: tokenId,
    };
    return this.httpMethods
      .postMethod(this.authBaseUrl + loginUrlType, data)
      .pipe(
        map((response) => {
          if (response) {
            this.setStorageData(response);
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  updatePasswordLoginUser(data: any) {
    return this.httpMethods
      .postMethod(this.authBaseUrl + 'update-password', data)
      .pipe(
        map((response) => {
          if (response) {
            let data = response?.data;
            if (data && Object.keys(data).length) {
              const data: any =
                this.setSessionStorage.getSessionData();
              if (data) {
                data.access_token = response.access_token;
                data.refresh_token = response.refresh_token;
                this.setSessionStorage.setSessionData(data);
              }
            }
          }
          return response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  resetPassword(data: any, token: any) {
    return this.httpMethods.postMethod(
      this.authBaseUrl + 'reset-password/' + token,
      data
    );
  }

  /**
   * ForPassword Api
   * @param data
   * @returns
   */
  forgotPassword(data: any) {
    return this.httpMethods.postMethod(
      this.authBaseUrl + 'forget-password',
      data
    );
  }

  /**
   * Get User List
   * @returns 
   */
  getCurrentUserDetails() {
    return this.httpMethods.getMethod('user/').pipe(map((response: UserDetails) => {
      if (!this.setSessionStorage.getCurrentRoles()) {
        this.setSessionStorage.updateCurrentRole(response.role);
      }
      return response;
    }), catchError((error:any) => {
      return throwError(error);
    }));
  }

  /**
   * User Data created and set into observable
   * @param response 
   */
  createUserData(response: any) {
    if (response) {
      let userData: UserDetails = response.data.user;
      this.currentUserDetails.next(userData)
    }
  }

  getProfileHandle() {
    return this.httpMethods.getMethod("user/profileHandle",true)
  }

  /**
   * Show Banner get api
   * @param date : date  format
   * @returns 
   */
  setOnboardingSeen() {
    return this.httpMethods.postMethod(`user/setOnboardingStatus` , {});
  }

  getParticularProducerData(storeHandle: any) {
    return this.httpMethods.getMethod(`producer/producer-details/${storeHandle}`);
  }
}
